// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useState, useEffect, useRef } from 'react';

// Material UI
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";

// Custom Components
import { AligningBox } from '../utils/AligningBox.js';
import LoadingIcon from "../loading/LoadingIcon.js";
import RenderIf from '../utils/RenderIf.js';
import { VideoControlButtons, VideoControlPlaybackBar } from "./VideoControls.js";
import { CustomVideo } from './PracticeVideo.js';

// Custom Scripts
import { LoadedDataState } from "../../scripts/loadedData/loadedData";
import { useFullscreenElement } from '../../scripts/fullscreen.js';
import { useVideoManager } from '../../scripts/video/videoManager.js';



// ----------------------------------------------------------------------------
//   Main Component
// ----------------------------------------------------------------------------


export function SingleRecordingPlayer({ recording }) {

  // --------------------------------------------------------------------------
  //   State
  // --------------------------------------------------------------------------

  const optionsBarHeight = '64px';

  // Control recording video state & playback
  const videoManager = useVideoManager( null );

  // Reference to the element that should be fullscreened
  const [ useFullscreen, fullscreenRef, setFullscreen ] = useFullscreenElement( null )


  // --------------------------------------------------------------------------
  //   Callbacks & Handlers
  // --------------------------------------------------------------------------

  // Handler for rewind button
  const onRewind = (sec) => {
    videoManager.moveBySeconds( -sec );
  }

  // Handler for progress bar seeking
  const onSeekPercent = (percent) => {
    videoManager.seekToPercent( percent, { holdUntilFinished: true, ignoreMax: true } );
  }

  // When seeking ends, resume the video
  const onSeekEnd = () => {
    videoManager.finishSeeking();
  }


  // --------------------------------------------------------------------------
  //   Component Layout
  // --------------------------------------------------------------------------

  return (
      <RenderIf condition={true} fallback={<LoadingIcon />}>
        <Stack ref={ fullscreenRef } height="100%" direction="column" minHeight="60vh">

          {/* Video */}
          <Box flexGrow={1} position="relative">
            <RenderIf condition={ recording?.state === LoadedDataState.SUCCESS }>
              <CustomVideo
                ref      = { videoManager.state.current.setVideoRef }
                videoURL = { recording?.value?.blob ? URL.createObjectURL( recording.value.blob ) : "" }
                usePoster = { false }
              />
            </RenderIf>
          </Box>
          {/* /Video */}

          {/* Playback Bar */}
          <VideoControlPlaybackBar
            progress    = { videoManager.state.current.progressPercent }
            maxProgress = { videoManager.state.current.maxProgressPercent }
            onChange    = { onSeekPercent }
            onSeekEnd   = { onSeekEnd }
            stopAtEnd   = { false }
            allowSkipAhead
          />

          {/* Controls */}
          <Box height={ optionsBarHeight } width="100%" bgcolor="white">
            <VideoControlButtons
              canStart      = { true }
              isPlaying     = { videoManager.state.current.isPlaying }
              isRecording   = { false }
              onPlay        = { videoManager.play }
              onStop        = { videoManager.pause }
              onRewind      = { onRewind }
              useFullscreen = { useFullscreen }
              setFullscreen = { setFullscreen }
            />
          </Box>
          {/* /Controls */}

        </Stack>
      </RenderIf>
  )
}



// ----------------------------------------------------------------------------
//   Wrapper Components
// ----------------------------------------------------------------------------


export function SingleRecordingCard({ recording }) {
  return (
    <Card h="100%" sx={{position: "relative"}}>
      <SingleRecordingPlayer recording={ recording } />
    </Card>
  )
}


export function SingleRecordingModal({ recording, ...dialogProps }) {
  return (
    <Dialog
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: '80vw' } }}
      { ...dialogProps }
    >
      <Stack direction="column">
        <DialogTitle variant="h4" component="h3">
          Recording { recording?.value?.id }: { recording?.value?.title }
        </DialogTitle>
        <SingleRecordingPlayer recording={ recording } />
      </Stack>
    </Dialog>
  )
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default { SingleRecordingCard };
